import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import moment from "moment";
import { Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";

import { User, UserTab } from "../../../auth/models";
import { ToastService } from "../../../main/services";
import { Tender } from "../../models";
import { TenderAwardsService } from "../../services";
import { SendConfirmationReminderResponse } from "../../services/tender-awards.service";
import { LocationTagsService } from "../../services/location-tags/location-tags.service";
import { TenderContractSuppliersService } from "../../services/tender-contract-suppliers/tender-contract-suppliers.service";
import { EditAwardModalComponent } from "./components/edit-award-modal/edit-award-modal.component";
import { EmailPreviewModalComponent } from "./components/email-preview-modal/email-preview-modal.component";

@Component({
    selector: "jft-tender-awards",
    templateUrl: "./tender-awards.component.html",
    styleUrls: ["./tender-awards.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class TenderAwardsComponent implements OnInit, OnDestroy {
    public loading: boolean = false;
    public tender: Tender;
    public supplierGroups: any[] = [];

    // Modal references
    @ViewChild("confirmAwardModal") confirmAwardModal!: TemplateRef<any>;
    @ViewChild("cancelTentativeAwardModal")
    cancelTentativeAwardModal!: TemplateRef<any>;
    @ViewChild("cancelAwardModal") cancelAwardModal!: TemplateRef<any>;
    @ViewChild("confirmBulkTentativeModal")
    confirmBulkTentativeModal!: TemplateRef<any>;
    @ViewChild("notificationValidationModal")
    notificationValidationModal!: TemplateRef<any>;

    // Modal state
    public offerToConfirm: any = null;
    public offerToConfirmSupplier: any = null;
    public offerToCancel: any = null;
    public cancellingAward: boolean = false;
    public confirmingAward: boolean = false;
    public confirmAwardComment: string = "";
    // Keep in sync with AWARD_COMMENT_MAX_LENGTH in award_comment.py
    public readonly awardCommentMaxLength = 2000;

    // Bulk confirmation state
    public bulkConfirmCount: number = 0;
    public bulkConfirmSupplier: any = null;
    public bulkTentativeOffers: any[] = [];
    public confirmingBulkAwards: boolean = false;

    /** Whether any offer in the bulk-confirm list carries an award comment. */
    public get hasBulkAwardComments(): boolean {
        return this.bulkTentativeOffers.some((o: any) => !!o?.award_comment);
    }

    // Export state
    public exporting: boolean = false;

    // Notification validation state
    public validationTentativeCount: number = 0;
    public validationFinalCount: number = 0;
    public validationSupplierGroup: any = null;

    // Pagination
    public currentPage: number = 1;
    public pageSize: number = 10;
    public totalItems: number = 0;
    public totalPages: number = 0;
    public totalOffers: number = 0;
    public totalSuppliers: number = 0;
    public tentativelyAwardedCount: number = 0;
    public awardedCount: number = 0;

    // Search and filters
    public searchTerm: string = "";
    public statusFilter: string = "";
    public notificationFilter: string = "";
    public selectedSuppliers: any[] = [];
    public selectedFuelTypes: any[] = [];
    public selectedFlightTypes: any[] = [];
    public selectedLocations: any[] = [];
    public selectedLocationTags: number[] = [];
    public locationTagOptions: UserTab[] = [];

    // Sorting
    public orderField: string = "";
    public order: string = "";

    // Current supplier group for popover
    public currentSupplierGroup: any = null;

    private searchSubscription: Subscription;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        protected toast: ToastService,
        private awardsService: TenderAwardsService,
        private contractSuppliersService: TenderContractSuppliersService,
        private ngbModal: NgbModal,
        public user: User,
        private locationTagsService: LocationTagsService,
    ) {}

    public ngOnInit() {
        this.dataInit();
        this.initializeFromUrlParams();
        this.loadLocationTags();
        // Don't call loadAwards() here - let loadLocationTags() handle it
        // This ensures saved tags are loaded before fetching data

        if (this.tender && this.tender.id) {
            this.subscribeToSearch();
        } else {
            console.error(
                "Tender not found in route data:",
                this.route.snapshot.data,
            );
        }
    }

    public ngOnDestroy() {
        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
    }

    private dataInit() {
        const data = this.route.snapshot.data;
        const parentData = this.route.parent?.snapshot.data;

        // Try to get tender from current route data first, then parent
        this.tender = data["tender"] || parentData?.["tender"];
    }

    private initializeFromUrlParams() {
        const queryParams = this.route.snapshot.queryParams;

        // Initialize filters from URL parameters
        this.currentPage = parseInt(queryParams["page"]) || 1;
        this.pageSize = parseInt(queryParams["page_size"]) || 10;
        this.searchTerm = queryParams["search"] || "";
        this.statusFilter = queryParams["status"] || "";
        this.notificationFilter = queryParams["notification"] || "";
        this.orderField = queryParams["order_field"] || "";
        this.order = queryParams["order"] || "";

        // Initialize array filters
        if (queryParams["suppliers"]) {
            // Note: We'll need to resolve supplier objects from IDs later
            this.selectedSuppliers = queryParams["suppliers"]
                .split(",")
                .map((id: string) => ({ id: parseInt(id) }));
        }

        if (queryParams["fuel_types"]) {
            this.selectedFuelTypes = queryParams["fuel_types"]
                .split(",")
                .map((id: string) => ({ id: parseInt(id) }));
        }

        if (queryParams["flight_types"]) {
            this.selectedFlightTypes = queryParams["flight_types"]
                .split(",")
                .map((id: string) => ({ id: parseInt(id) }));
        }

        if (queryParams["locations"]) {
            this.selectedLocations = queryParams["locations"]
                .split(",")
                .map((iata: string) => ({ iata }));
        }
    }

    private updateUrlParams() {
        const queryParams: any = {};

        // Add non-default values to query params
        if (this.currentPage !== 1) queryParams["page"] = this.currentPage;
        if (this.pageSize !== 10) queryParams["page_size"] = this.pageSize;
        if (this.searchTerm) queryParams["search"] = this.searchTerm;
        if (this.statusFilter) queryParams["status"] = this.statusFilter;
        if (this.notificationFilter)
            queryParams["notification"] = this.notificationFilter;
        if (this.orderField) queryParams["order_field"] = this.orderField;
        if (this.order) queryParams["order"] = this.order;

        // Add array filters
        if (this.selectedSuppliers.length > 0) {
            queryParams["suppliers"] = this.selectedSuppliers
                .map((s) => s.id)
                .join(",");
        }
        if (this.selectedFuelTypes.length > 0) {
            queryParams["fuel_types"] = this.selectedFuelTypes
                .map((f) => f.id)
                .join(",");
        }
        if (this.selectedFlightTypes.length > 0) {
            queryParams["flight_types"] = this.selectedFlightTypes
                .map((f) => f.id)
                .join(",");
        }
        if (this.selectedLocations.length > 0) {
            queryParams["locations"] = this.selectedLocations
                .map((l) => l.iata)
                .join(",");
        }

        // Update URL without triggering navigation
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: queryParams,
            replaceUrl: true,
        });
    }

    private subscribeToSearch() {
        this.searchSubscription = this.awardsService.searchTermObservable
            .pipe(debounceTime(300), distinctUntilChanged())
            .subscribe((term: string) => {
                this.searchTerm = term;
                this.currentPage = 1; // Reset to first page on search
                this.updateUrlParams();
                this.loadAwards();
            });
    }

    public loadAwards() {
        if (!this.tender || !this.tender.id) {
            console.error(
                "Cannot load awards: tender or tender.id is undefined",
            );
            this.loading = false;
            return;
        }

        // Prevent multiple simultaneous requests
        if (this.loading) {
            return;
        }

        this.loading = true;

        const params = {
            page: this.currentPage,
            page_size: this.pageSize,
            term: this.searchTerm,
            status_filter: this.statusFilter,
            notification_filter: this.notificationFilter,
            order: this.order
                ? this.order === "ASC"
                    ? this.orderField
                    : `-${this.orderField}`
                : null,
            supplier_filter: this.selectedSuppliers.map((s) => s.id).join(","),
            fuel_type_filter: this.selectedFuelTypes.map((f) => f.id).join(","),
            flight_type_filter: this.selectedFlightTypes
                .map((f) => f.id)
                .join(","),
            location_filter: this.selectedLocations
                .map((l) => l.iata)
                .join(","),
            location_tag_filter: this.selectedLocationTags.join(","),
        };

        // Remove empty parameters
        Object.keys(params).forEach((key) => {
            if (!params[key] || params[key] === "") {
                delete params[key];
            }
        });

        this.awardsService.fetchAwards(this.tender.id, params).subscribe(
            (result) => {
                this.supplierGroups = result.results || [];
                this.totalItems = result.count || 0;
                this.currentPage = result.page || 1;
                this.pageSize = result.page_size || 10;
                this.totalPages = result.total_pages || 0;
                this.totalOffers = result.total_offers || 0;
                this.totalSuppliers = result.total_suppliers || 0;
                this.tentativelyAwardedCount =
                    result.tentatively_awarded_count || 0;
                this.awardedCount = result.awarded_count || 0;
                this.loading = false;
            },
            (error) => {
                this.toast.error("Failed to load awards data");
                this.loading = false;
            },
        );
    }

    public search(term: string) {
        this.awardsService.searchTermObservable.next(term);
    }

    public sort(event: Event, field: string) {
        event.preventDefault();

        if (this.orderField === field) {
            this.order = this.order === "ASC" ? "DESC" : "ASC";
        } else {
            this.orderField = field;
            this.order = "ASC";
        }

        this.currentPage = 1; // Reset to first page on sort
        this.updateUrlParams();
        this.loadAwards();
    }

    public onPageChange(page: number) {
        this.currentPage = page;
        this.updateUrlParams();
        this.loadAwards();
    }

    public onPageSizeChange(pageSize: number) {
        this.pageSize = pageSize;
        this.currentPage = 1; // Reset to first page
        this.updateUrlParams();
        this.loadAwards();
    }

    public get startIndex(): number {
        return (this.currentPage - 1) * this.pageSize + 1;
    }

    public get endIndex(): number {
        return Math.min(this.currentPage * this.pageSize, this.totalItems);
    }

    public clearFilters() {
        this.selectedSuppliers = [];
        this.selectedFuelTypes = [];
        this.selectedFlightTypes = [];
        this.selectedLocations = [];
        this.selectedLocationTags = [];
        // Clear saved location tags from localStorage
        this.locationTagsService.clearSelectedTags();
        this.searchTerm = "";
        this.statusFilter = "";
        this.notificationFilter = "";
        this.orderField = "";
        this.order = "";
        this.currentPage = 1;

        // Clear URL parameters as well
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {},
            replaceUrl: true,
        });

        this.loadAwards();
    }

    public getTotalAwardedVolume(): number {
        return this.supplierGroups.reduce((total, group) => {
            return total + (group.total_awarded_volume || 0);
        }, 0);
    }

    public getTenderUnits(): string {
        // Get units from tender object
        if (this.tender?.units?.code) {
            return this.tender.units.code;
        }
        // Fallback to analytic settings units if available (using any type for flexibility)
        if ((this.tender as any)?.analytic_setting?.units?.code) {
            return (this.tender as any).analytic_setting.units.code;
        }
        // Default fallback
        return "UNITS";
    }

    public getTotalAwardedPercent(): number {
        return this.supplierGroups.reduce((total, group) => {
            return total + (group.total_awarded_percent || 0);
        }, 0);
    }

    public hasTentativelyAwardedOffers(supplierGroup: any): boolean {
        return supplierGroup.offers.some(
            (offer: any) =>
                offer.status === "TENTATIVE_AWARDED" ||
                offer.status === "TENTATIVELY_AWARDED",
        );
    }

    public hasAwardedOffers(supplierGroup: any): boolean {
        return supplierGroup.offers.some(
            (offer: any) =>
                offer.status === "AWARDED" ||
                offer.status === "TENTATIVE_AWARDED" ||
                offer.status === "TENTATIVELY_AWARDED",
        );
    }

    public getNotificationButtonText(supplierGroup: any): string {
        // Check if any offers in this supplier group have been notified
        const hasNotifiedOffers = supplierGroup.offers.some(
            (offer: any) => offer.notification_sent,
        );
        return hasNotifiedOffers ? "Resend Notification" : "Notify Supplier";
    }

    public hasNotificationBeenSent(supplierGroup: any): boolean {
        // Check if any offers in this supplier group have been notified
        return supplierGroup.offers.some(
            (offer: any) => offer.notification_sent,
        );
    }

    // --- Award-confirmation reminder (tender-level nudge to suppliers) ---

    /** Unconfirmed = fully AWARDED (not tentative) and supplier bid hasn't been e-signed. */
    private isUnconfirmedAwarded(offer: any): boolean {
        return offer?.status === "AWARDED" && !offer?.bid?.has_signature;
    }

    /** Surface the reminder button whenever at least one supplier has a
     *  finalized (non-tentative) award they haven't e-signed yet. The backend
     *  endpoint filters to status=AWARDED only, so tentatives on other rows
     *  don't get pulled into the reminder batch. */
    public get canSendConfirmationReminder(): boolean {
        if (this.loading) return false;
        return this.unconfirmedSupplierCount > 0;
    }

    /** Count of distinct suppliers with at least one unconfirmed awarded offer. */
    public get unconfirmedSupplierCount(): number {
        return this.supplierGroups.filter((group) =>
            group.offers.some((o: any) => this.isUnconfirmedAwarded(o)),
        ).length;
    }

    /** Most recent `confirmation_reminder_sent_at` across the unconfirmed set,
     *  used to show "last reminded N hours ago" in the soft-warning modal. */
    public get lastReminderSentAt(): Date | null {
        let latest: Date | null = null;
        for (const group of this.supplierGroups) {
            for (const offer of group.offers) {
                if (
                    !this.isUnconfirmedAwarded(offer) ||
                    !offer.confirmation_reminder_sent_at
                )
                    continue;
                const when = new Date(offer.confirmation_reminder_sent_at);
                if (!latest || when > latest) latest = when;
            }
        }
        return latest;
    }

    /** Human-friendly "2h ago", "3d ago" for the confirm-reminder modal. */
    public get lastReminderRelative(): string | null {
        const at = this.lastReminderSentAt;
        if (!at) return null;
        const diffMs = Date.now() - at.getTime();
        const mins = Math.floor(diffMs / 60000);
        if (mins < 1) return "just now";
        if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
        const hours = Math.floor(mins / 60);
        if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
        const days = Math.floor(hours / 24);
        return `${days} day${days === 1 ? "" : "s"} ago`;
    }

    public sendingConfirmationReminder: boolean = false;

    public openConfirmationReminderModal(content: TemplateRef<any>): void {
        this.ngbModal
            .open(content, { centered: true, backdrop: "static" })
            .result.then(
                (confirmed: boolean) => {
                    if (!confirmed) return;
                    this.sendingConfirmationReminder = true;
                    this.awardsService
                        .sendConfirmationReminder(this.tender.id)
                        .subscribe({
                            next: (resp) => {
                                this.sendingConfirmationReminder = false;
                                this.handleReminderResponse(resp);
                                this.loadAwards();
                            },
                            error: (err) => {
                                this.sendingConfirmationReminder = false;
                                this.toast.error(
                                    err?.error?.detail ||
                                        "Failed to send reminder.",
                                );
                            },
                        });
                },
                () => {
                    /* dismissed */
                },
            );
    }

    private handleReminderResponse(
        resp: SendConfirmationReminderResponse,
    ): void {
        const s = resp.suppliers_notified;
        if (s === 0 && resp.reason === "already_confirmed") {
            this.toast.info(resp.detail || "All awards are already confirmed.");
            return;
        }
        if (s === 0 && resp.reason === "rate_limited") {
            this.toast.warning(
                resp.detail ||
                    "Reminders were sent recently - try again later.",
            );
            return;
        }
        const pluralS = s === 1 ? "" : "s";
        if (resp.suppliers_rate_limited) {
            const r = resp.suppliers_rate_limited;
            this.toast.success(
                `Reminder sent to ${s} supplier${pluralS}. ${r} skipped (reminded within the last hour).`,
            );
        } else {
            this.toast.success(`Reminder sent to ${s} supplier${pluralS}.`);
        }
    }

    public getNotificationTooltip(supplierGroup: any): string {
        // Check if we have notification details from the backend
        if (supplierGroup.notification_details) {
            const details = supplierGroup.notification_details;
            const sentAt = details.sent_at
                ? new Date(details.sent_at).toLocaleString()
                : "Unknown time";
            const emails = details.recipient_emails || [];
            const supplierName = details.supplier_name || "Unknown Supplier";

            let tooltip = `Notification sent to ${supplierName}\n`;
            tooltip += `Sent: ${sentAt}\n`;

            if (emails.length > 0) {
                tooltip += `Recipients: ${emails.join(", ")}`;
            } else {
                tooltip += "Recipients: No active users found";
            }

            return tooltip;
        }

        // Fallback for when notification_details is not available
        const notifiedOffer = supplierGroup.offers.find(
            (offer: any) => offer.notification_sent,
        );

        if (!notifiedOffer) {
            return "No notification sent";
        }

        const supplierName = supplierGroup.supplier?.name || "Unknown Supplier";
        const sentAt = notifiedOffer.notification_sent_at
            ? new Date(notifiedOffer.notification_sent_at).toLocaleString()
            : "Unknown time";

        return `Notification sent to ${supplierName}\nSent: ${sentAt}`;
    }

    public getNotificationPopoverContent(supplierGroup: any): string {
        // Check if we have notification details from the backend
        if (supplierGroup.notification_details) {
            const details = supplierGroup.notification_details;
            const sentAt = details.sent_at
                ? new Date(details.sent_at).toLocaleString()
                : "Unknown time";
            const emails = details.recipient_emails || [];
            const supplierName = details.supplier_name || "Unknown Supplier";

            let content = `<div class="notification-popover">`;
            content += `<div class="mb-2"><strong>${supplierName}</strong></div>`;
            content += `<div class="mb-2 text-muted small">Sent: ${sentAt}</div>`;

            if (emails.length > 0) {
                content += `<div class="mb-1 small"><strong>Recipients:</strong></div>`;
                content += `<table class="table table-sm table-borderless mb-0">`;
                content += `<tbody>`;
                emails.forEach((email) => {
                    content += `<tr><td class="py-1 px-0 small">${email}</td></tr>`;
                });
                content += `</tbody></table>`;
            } else {
                content += `<div class="text-muted small">No active users found</div>`;
            }

            content += `</div>`;
            return content;
        }

        // Fallback for when notification_details is not available
        const notifiedOffer = supplierGroup.offers.find(
            (offer: any) => offer.notification_sent,
        );

        if (!notifiedOffer) {
            return '<div class="text-muted">No notification sent</div>';
        }

        const supplierName = supplierGroup.supplier?.name || "Unknown Supplier";
        const sentAt = notifiedOffer.notification_sent_at
            ? new Date(notifiedOffer.notification_sent_at).toLocaleString()
            : "Unknown time";

        let content = `<div class="notification-popover">`;
        content += `<div class="mb-2"><strong>${supplierName}</strong></div>`;
        content += `<div class="text-muted small">Sent: ${sentAt}</div>`;
        content += `</div>`;

        return content;
    }

    public setCurrentSupplierGroup(supplierGroup: any): void {
        this.currentSupplierGroup = supplierGroup;
    }

    public getNotificationSupplierName(supplierGroup: any): string {
        if (!supplierGroup) return "Unknown Supplier";
        if (supplierGroup.notification_details) {
            return (
                supplierGroup.notification_details.supplier_name ||
                "Unknown Supplier"
            );
        }
        return supplierGroup.supplier?.name || "Unknown Supplier";
    }

    public getNotificationSentTime(supplierGroup: any): string {
        if (!supplierGroup) return "Unknown time";
        if (
            supplierGroup.notification_details &&
            supplierGroup.notification_details.sent_at
        ) {
            return new Date(
                supplierGroup.notification_details.sent_at,
            ).toLocaleString();
        }

        // Fallback to offer-level timestamp
        const notifiedOffer = supplierGroup.offers.find(
            (offer: any) => offer.notification_sent,
        );
        if (notifiedOffer && notifiedOffer.notification_sent_at) {
            return new Date(
                notifiedOffer.notification_sent_at,
            ).toLocaleString();
        }

        return "Unknown time";
    }

    public getNotificationEmails(supplierGroup: any): string[] {
        if (!supplierGroup) return [];
        if (
            supplierGroup.notification_details &&
            supplierGroup.notification_details.recipient_emails
        ) {
            return supplierGroup.notification_details.recipient_emails;
        }
        return [];
    }

    public confirmSupplierAwards(supplierGroup: any) {
        const tentativeOffers = supplierGroup.offers.filter(
            (offer: any) =>
                offer.status === "TENTATIVE_AWARDED" ||
                offer.status === "TENTATIVELY_AWARDED",
        );

        if (tentativeOffers.length === 0) {
            this.toast.warning(
                "No tentatively awarded offers found for this supplier",
            );
            return;
        }

        // Set modal data
        this.bulkConfirmCount = tentativeOffers.length;
        this.bulkConfirmSupplier = supplierGroup.supplier;
        this.bulkTentativeOffers = tentativeOffers;

        // Open confirmation modal
        this.ngbModal.open(this.confirmBulkTentativeModal, {
            size: "lg",
            backdrop: "static",
            centered: true,
        });
    }

    public confirmBulkTentativeAction() {
        this.confirmingBulkAwards = true;
        let completedCount = 0;
        const totalCount = this.bulkTentativeOffers.length;

        this.bulkTentativeOffers.forEach((offer: any) => {
            this.awardsService
                .confirmAward(this.tender.id, offer.id)
                .subscribe({
                    next: () => {
                        completedCount++;
                        if (completedCount === totalCount) {
                            this.confirmingBulkAwards = false;
                            this.toast.success(
                                `Successfully confirmed ${totalCount} award(s) for ${this.bulkConfirmSupplier.name}`,
                            );
                            this.loadAwards(); // Reload the data
                            this.ngbModal.dismissAll();
                            // Reset state
                            this.bulkConfirmCount = 0;
                            this.bulkConfirmSupplier = null;
                            this.bulkTentativeOffers = [];
                        }
                    },
                    error: () => {
                        this.confirmingBulkAwards = false;
                        this.toast.error(
                            `Failed to confirm award for offer ${offer.id}`,
                        );
                        this.ngbModal.dismissAll();
                        // Reset state
                        this.bulkConfirmCount = 0;
                        this.bulkConfirmSupplier = null;
                        this.bulkTentativeOffers = [];
                    },
                });
        });
    }

    public confirmAllAwards() {
        // TODO: Implement award confirmation functionality
        this.toast.warning(
            "Award confirmation functionality will be implemented in Stage 9",
        );
    }

    public confirmAward(offer: any) {
        this.offerToConfirm = offer;
        // Confirming is what makes the comment visible to the supplier, so it
        // stays editable right up to that click.
        this.confirmAwardComment = offer?.award_comment || "";

        // Find the supplier for this offer
        this.offerToConfirmSupplier = null;
        for (const supplierGroup of this.supplierGroups) {
            if (supplierGroup.offers.some((o: any) => o.id === offer.id)) {
                this.offerToConfirmSupplier = supplierGroup.supplier;
                break;
            }
        }

        this.ngbModal.open(this.confirmAwardModal, {
            size: "lg",
            backdrop: "static",
            centered: true,
        });
    }

    public confirmAwardAction() {
        if (this.offerToConfirm) {
            this.confirmingAward = true;
            this.awardsService
                .confirmAward(
                    this.tender.id,
                    this.offerToConfirm.id,
                    (this.confirmAwardComment || "").trim(),
                )
                .subscribe({
                    next: () => {
                        this.confirmingAward = false;
                        this.toast.success("Award confirmed successfully");
                        this.loadAwards(); // Reload the data
                        this.ngbModal.dismissAll();
                        this.offerToConfirm = null;
                        this.offerToConfirmSupplier = null;
                        this.confirmAwardComment = "";
                    },
                    error: () => {
                        this.confirmingAward = false;
                        this.toast.error("Failed to confirm award");
                        this.ngbModal.dismissAll();
                        this.offerToConfirm = null;
                        this.offerToConfirmSupplier = null;
                    },
                });
        }
    }

    public cancelTentativeAward(offer: any) {
        this.offerToCancel = offer;
        this.ngbModal.open(this.cancelTentativeAwardModal, {
            size: "lg",
            backdrop: "static",
            centered: true,
        });
    }

    public cancelTentativeAwardAction() {
        if (this.offerToCancel) {
            this.cancellingAward = true;
            this.awardsService
                .cancelTentativeAward(this.tender.id, this.offerToCancel.id)
                .subscribe({
                    next: () => {
                        this.cancellingAward = false;
                        this.toast.success(
                            "Tentative award cancelled successfully",
                        );
                        this.loadAwards(); // Reload the data
                        this.ngbModal.dismissAll();
                        this.offerToCancel = null;
                    },
                    error: () => {
                        this.cancellingAward = false;
                        this.toast.error("Failed to cancel tentative award");
                        this.ngbModal.dismissAll();
                        this.offerToCancel = null;
                    },
                });
        }
    }

    public cancelAward(offer: any) {
        this.offerToCancel = offer;
        this.ngbModal.open(this.cancelAwardModal, {
            size: "lg",
            backdrop: "static",
            centered: true,
        });
    }

    public cancelAwardAction() {
        if (this.offerToCancel) {
            this.cancellingAward = true;
            // Use the correct cancel award endpoint for regular awards
            this.awardsService
                .cancelAward(
                    this.tender.id,
                    this.offerToCancel.contract_id,
                    this.offerToCancel.id,
                )
                .subscribe({
                    next: () => {
                        this.cancellingAward = false;
                        this.toast.success("Award cancelled successfully");
                        this.loadAwards(); // Reload the data
                        this.ngbModal.dismissAll();
                        this.offerToCancel = null;
                    },
                    error: () => {
                        this.cancellingAward = false;
                        this.toast.error("Failed to cancel award");
                        this.ngbModal.dismissAll();
                        this.offerToCancel = null;
                    },
                });
        }
    }

    public editAward(offer: any) {
        // Store the offer ID to find it after reload
        const offerId = offer.id;

        // Reload awards to get fresh data, then open modal
        this.reloadAwardsForEdit(offerId);
    }

    private reloadAwardsForEdit(offerId: number) {
        this.loading = true;

        const params = {
            page: this.currentPage,
            page_size: this.pageSize,
            term: this.searchTerm,
            status_filter: this.statusFilter,
            notification_filter: this.notificationFilter,
            order: this.order
                ? this.order === "ASC"
                    ? this.orderField
                    : `-${this.orderField}`
                : null,
            supplier_filter: this.selectedSuppliers.map((s) => s.id).join(","),
            fuel_type_filter: this.selectedFuelTypes.map((f) => f.id).join(","),
            flight_type_filter: this.selectedFlightTypes
                .map((f) => f.id)
                .join(","),
            location_filter: this.selectedLocations
                .map((l) => l.iata)
                .join(","),
        };

        // Remove empty parameters
        Object.keys(params).forEach((key) => {
            if (!params[key] || params[key] === "") {
                delete params[key];
            }
        });

        this.awardsService.fetchAwards(this.tender.id, params).subscribe({
            next: (result) => {
                this.supplierGroups = result.results || [];
                this.totalItems = result.count || 0;
                this.currentPage = result.page || 1;
                this.pageSize = result.page_size || 10;
                this.totalPages = result.total_pages || 0;
                this.totalOffers = result.total_offers || 0;
                this.totalSuppliers = result.total_suppliers || 0;
                this.tentativelyAwardedCount =
                    result.tentatively_awarded_count || 0;
                this.awardedCount = result.awarded_count || 0;
                this.loading = false;

                // Find the updated offer and open the modal
                const updatedOffer = this.findOfferById(offerId);
                if (updatedOffer) {
                    this.openEditModal(updatedOffer);
                } else {
                    this.toast.error("Offer not found after reload");
                }
            },
            error: (error) => {
                this.toast.error("Failed to load awards data");
                this.loading = false;
            },
        });
    }

    private findOfferById(offerId: number): any {
        for (const supplierGroup of this.supplierGroups) {
            const offer = supplierGroup.offers.find(
                (o: any) => o.id === offerId,
            );
            if (offer) {
                return offer;
            }
        }
        return null;
    }

    private openEditModal(offer: any) {
        const modalRef = this.ngbModal.open(EditAwardModalComponent, {
            size: "lg",
            backdrop: "static",
            centered: true,
        });

        // Find the supplier group for this offer
        const supplierGroup = this.getSupplierGroupForOffer(offer);

        modalRef.componentInstance.tender = this.tender;
        modalRef.componentInstance.offer = offer;
        modalRef.componentInstance.supplierGroup = supplierGroup;

        modalRef.result.then(
            (result) => {
                if (result && result.updated) {
                    // Award was successfully updated, reload the data
                    this.loadAwards();
                }
            },
            (dismissed) => {
                // Modal was dismissed/cancelled
            },
        );
    }

    public notifySupplier(supplierGroup: any) {
        // Only get fully awarded offers (no tentative awards)
        const finalOffers = supplierGroup.offers.filter(
            (offer: any) => offer.status === "AWARDED",
        );

        // Check for tentative awards
        const tentativeOffers = supplierGroup.offers.filter(
            (offer: any) =>
                offer.status === "TENTATIVE_AWARDED" ||
                offer.status === "TENTATIVELY_AWARDED",
        );

        if (finalOffers.length === 0) {
            this.toast.warning(
                "No fully awarded locations found for this supplier",
            );
            return;
        }

        // If there are tentative awards, show validation modal
        if (tentativeOffers.length > 0) {
            this.validationTentativeCount = tentativeOffers.length;
            this.validationFinalCount = finalOffers.length;
            this.validationSupplierGroup = supplierGroup;

            this.ngbModal.open(this.notificationValidationModal, {
                size: "lg",
                backdrop: "static",
                centered: true,
            });
            return;
        }

        // Only proceed with fully awarded offers
        this.openEmailPreviewModal(supplierGroup, finalOffers);
    }

    private openEmailPreviewModal(supplierGroup: any, awardedOffers: any[]) {
        const modalRef = this.ngbModal.open(EmailPreviewModalComponent, {
            size: "lg",
            backdrop: "static",
            centered: true,
        });

        modalRef.componentInstance.tender = this.tender;
        modalRef.componentInstance.supplierGroup = supplierGroup;
        modalRef.componentInstance.awardedOffers = awardedOffers;

        modalRef.result.then(
            (result) => {
                if (result && result.sent) {
                    // Email was sent successfully - toast message is already shown in modal
                    // No need to show duplicate toast here
                    this.loadAwards();
                }
            },
            (dismissed) => {
                // Modal was dismissed/cancelled
            },
        );
    }

    public exportAwards() {
        if (!this.tender || !this.tender.id) {
            this.toast.error("Cannot export: tender not found");
            return;
        }

        this.exporting = true;

        // Mirror the filters the user has applied on the page so the export
        // contains the same rows they can see. Paging is deliberately omitted
        // - the export always spans the full filtered set.
        const params = {
            term: this.searchTerm,
            status_filter: this.statusFilter,
            notification_filter: this.notificationFilter,
            supplier_filter: this.selectedSuppliers.map((s) => s.id).join(","),
            fuel_type_filter: this.selectedFuelTypes.map((f) => f.id).join(","),
            flight_type_filter: this.selectedFlightTypes
                .map((f) => f.id)
                .join(","),
            location_filter: this.selectedLocations
                .map((l) => l.iata)
                .join(","),
            location_tag_filter: this.selectedLocationTags.join(","),
        };
        Object.keys(params).forEach((key) => {
            if (!params[key]) {
                delete params[key];
            }
        });

        this.awardsService
            .exportAwardsToExcel(this.tender.id, params)
            .subscribe({
                next: (result) => {
                    this.exporting = false;

                    if (result.type === "task") {
                        this.toast.info(
                            "Export started. You'll be notified when the file is ready.",
                        );
                    } else if (result.type === "blob" && result.blob) {
                        // Direct download - create link and download
                        const url = window.URL.createObjectURL(result.blob);
                        const link = document.createElement("a");
                        link.href = url;
                        link.download = `${this.tender.name || "tender"}-awards.xlsx`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                        this.toast.success(
                            "Excel file downloaded successfully",
                        );
                    }
                },
                error: (error) => {
                    this.exporting = false;
                    if (error?.status === 404) {
                        this.toast.info("No awards match the current filters.");
                        return;
                    }
                    console.error("Export error:", error);
                    this.toast.error("Failed to export awards to Excel");
                },
            });
    }

    public onSearchChange() {
        // Use the search service observable instead of creating a new subscription
        this.awardsService.searchTermObservable.next(this.searchTerm);
    }

    public onFilterChange() {
        this.currentPage = 1;
        this.updateUrlParams();
        this.loadAwards();
    }

    // TrackBy functions for performance optimization
    public trackBySupplierGroup(index: number, item: any): any {
        return item.supplier_id || index;
    }

    public trackByOffer(index: number, item: any): any {
        return item.id || index;
    }

    public getSupplierGroupForOffer(offer: any): any {
        if (!offer || !this.supplierGroups) {
            return null;
        }

        for (const supplierGroup of this.supplierGroups) {
            const foundOffer = supplierGroup.offers.find(
                (o: any) => o.id === offer.id,
            );
            if (foundOffer) {
                return supplierGroup;
            }
        }
        return null;
    }

    // Helper methods for modal data
    public getAwardedVolumeFormatted(offer: any): string {
        if (offer.awarded_volume != null) {
            return Number(offer.awarded_volume).toLocaleString("en-US", {
                maximumFractionDigits: 0,
            });
        }
        if (!offer.volume || !offer.awarded_percent) {
            return "0";
        }
        const awardedVolume = (offer.volume * offer.awarded_percent) / 100;
        return awardedVolume.toLocaleString("en-US", {
            maximumFractionDigits: 0,
        });
    }

    public getContractStartDate(offer: any): string {
        if (!offer || !offer.contract_start) return "";
        // Use moment.parseZone to preserve the date without timezone conversion
        // This prevents the one-day offset issue
        return moment.parseZone(offer.contract_start).format("DD/MM/YYYY");
    }

    public getContractEndDate(offer: any): string {
        if (!offer || !offer.contract_end) return "";
        // Use moment.parseZone to preserve the date without timezone conversion
        // This prevents the one-day offset issue
        return moment.parseZone(offer.contract_end).format("DD/MM/YYYY");
    }

    public getValidationFinalOffers(): any[] {
        if (!this.validationSupplierGroup) return [];
        return this.validationSupplierGroup.offers.filter(
            (offer: any) => offer.status === "AWARDED",
        );
    }

    public getValidationTentativeOffers(): any[] {
        if (!this.validationSupplierGroup) return [];
        return this.validationSupplierGroup.offers.filter(
            (offer: any) =>
                offer.status === "TENTATIVE_AWARDED" ||
                offer.status === "TENTATIVELY_AWARDED",
        );
    }

    // Location tags methods
    private loadLocationTags() {
        this.locationTagsService.fetchLocationTags().subscribe(
            (tags: UserTab[]) => {
                this.locationTagOptions = tags;
                // Load previously selected tags from localStorage and validate them
                const savedTags = this.locationTagsService.loadSelectedTags();
                this.selectedLocationTags =
                    this.locationTagsService.validateSelectedTags(
                        savedTags,
                        tags,
                    );
                // Always fetch data after tags are loaded (whether tags exist or not)
                this.loadAwards();
            },
            (error) => {
                console.error("Failed to load location tags:", error);
                this.selectedLocationTags = [];
                // Even on error, fetch data without tag filter
                this.loadAwards();
            },
        );
    }

    public onLocationTagFilterChange() {
        // Save selected tags to localStorage for this user
        this.locationTagsService.saveSelectedTags(this.selectedLocationTags);
        this.currentPage = 1;
        this.loadAwards();
    }
}
